import { push } from 'redux-first-history';
import Long from 'long';
import { v4 as uuidv4 } from 'uuid';
import packageInfo from '../../package.json';

import * as types from '../constants/cidraActions';
import getErrorMessage from '../constants/getMessage';
import {
  startLoading,
  finishLoading,
  setServerState,
  doLogout,
  doSetSelfServiceEnvironment,
  doLoginFailure,
  doSetInitialTempCode
} from './shared';
import { decideNextStep } from './decideNextStep';
import { doVerifyPassword } from './authentication';
import { IID_PUSH } from '../constants/contexts';
import {
  cidMobile,
  cidra,
  sba,
  StartSessionProtocols,
  ChooseModalityProtocols,
  GetMessageProtocols,
  piw
} from '../constants/clientProtocols';
import {
  doGetSkipSecondFactorToken, setSkipSecondFactorToken, queueTokenDeletion
} from './skipSecondFactor';
import { logSsf } from '../util/log';
import { CloudAuthApplexClient } from '@imprivata/cloudauth-applex-client';
import { getCidMobileErrorString } from '../constants/cidMobileErrorMap';
import {
  doChangeModality, doInputOtp, doInputPassword
} from './chooseModality';
import {
  initiateImprivataIdPush, resendEmail, resendSms, initiateIdv,
  setInitialUsername, setOptionalWorkflowNameAndRequestContext, verifyPassword, setAccountToken
} from './index';
import {
  EPCS_WORKFLOW, KNOWLEDGE_ACR_VALUE, INHERENCE_ACR_VALUE, POSSESSION_ACR_VALUE, POSSESSION_OR_INHERENCE_ACR_VALUE,
  KNOWLEDGE_OR_POSSESSION_ACR_VALUE, KNOWLEDGE_OR_INHERENCE_ACR_VALUE, KNOWLEDGE_OR_POSSESSION_OR_INHERENCE_ACR_VALUE,
  SECONDFACTORONLY_WORKFLOW, HELPDESKVERIFICATION_WORKFLOW
} from '../constants/workflowConstants';
import { queueStateTokenDeletion } from './resumeAuthSession';


const SERVER_TIMEOUT = 10 * 60 * 1000;

// The session version that the push completed handler will be gated on.
// If the current redux session version differs from this number, we will
// not switch to manual, because that means the user logged out or failed
// authentication in between starting the push and the moment the handler
// is called.
let expectedSessionVersion = 0;

export function getAuthClientWithAddress(authClient, oneSignTenantId, oneSignUserId) {
  const {
    StartInterplexId, StartUserTargetedAddress, StartInterplexAddress,
  } = StartSessionProtocols;
  if (authClient) {
    const osTenantLong = Long.fromString(oneSignTenantId);
    const osUserLong = Long.fromString(oneSignUserId);
    const interplexId = StartInterplexId;
    const userTargetedAddress = new StartUserTargetedAddress(osTenantLong, osUserLong).asProxiedInterplexAddress;
    const newAddress = new StartInterplexAddress(interplexId.value, userTargetedAddress);
    return authClient.withNewTargetAddress(newAddress);
  }
  else {
    return authClient;
  }
}

// Following params are for cidMobile service only:
// username
// workflow
// optionalCidMobileServiceInfo: [
// optionalPartnerName = required
// optionalAppName = required
// optionalTransactionId = (actually optional)
// ]

export function doStartSession(
  accountToken, cloudauthEnvironment = null, selfServiceEnvironment = null, username = null,
  workflow = null, cidMobileServiceInfo = []
) {
  let session;
  return function (dispatch, getState) {
    dispatch(setAccountToken(accountToken, cloudauthEnvironment, selfServiceEnvironment) );
    const { StartPushCompletedState, StartIdvCompletedState, } = StartSessionProtocols;
    const { GetMsgInvalidCredentials, GetMsgInternalError, } = GetMessageProtocols;

    // set a tracer ID with a random GUID
    const sessionTraceId = uuidv4();

    dispatch(doLoginStart() );
    dispatch(doStartSessionEstablishing(true) );
    // trace id will be used for observability
    dispatch(doSetSessionTraceId(sessionTraceId) );

    if (selfServiceEnvironment !== null) {
      dispatch(doSetSelfServiceEnvironment(selfServiceEnvironment) );
    }

    let decodedToken;
    try {
      decodedToken = JSON.parse(atob(accountToken) );
    }
    catch (e) {
      decodedToken = {};
    }
    let workflowValue = decodedToken?.workflow;

    const isHelpdeskPath = document.location.pathname === '/helpdesk.html';
    const isHelpdeskWorkflow = workflowValue === HELPDESKVERIFICATION_WORKFLOW;
    const hasVerificationSessionId = !!decodedToken?.requestContext?.verificationSessionId;

    // check if there are any signs of a helpdesk verification workflow
    // if so (pathname or workflow name or verificationSessionId),
    // set the workflow to HELPDESKVERIFICATION_WORKFLOW for proper error handling.
    if (isHelpdeskPath || isHelpdeskWorkflow || hasVerificationSessionId) {
      dispatch(setOptionalWorkflowNameAndRequestContext(
        HELPDESKVERIFICATION_WORKFLOW,
        decodedToken.requestContext || null
      ) );
    }
    const { sessionVersion, isHelpdeskVerificationWorkflow, } = getState().login;

    const onConnectionListener = function (event) {
      if (event) {
        console.log('Received the ' + event.code + ' event');
        const { authClient, isHelpdeskVerificationWorkflow, } = getState().login;
        switch (event.code) {
        case 'connectionEstablished':
          const oneSignTenantId = event.connectionContext.oneSignTenantId;
          const oneSignUserId = event.connectionContext.oneSignUserId;
          const sessionService = event.connectionContext.service;
          const newAuthClient = getAuthClientWithAddress(authClient, oneSignTenantId, oneSignUserId);
          dispatch(doSessionConnected(oneSignTenantId, oneSignUserId, sessionService, newAuthClient) );
          dispatch(startNewSessionHandler(decodedToken, username, workflow, cidMobileServiceInfo) );
          return;
          // set server state? find out where the OS tenant/user is already set
        case 'connectionUnavailable':
        case 'unauthorizedApplexUser':
          // need to check if we got data for cidMobileServiceInfo
          // because we do not have sessionService data at this point
          if (cidMobileServiceInfo.optionalAppName !== undefined && cidMobileServiceInfo.optionalPartnerName !== undefined) {
            console.log('Error when establishing applex connection for CidMobile. Redirect to application');
            let cidErrorCode = 'InternalError';
            if (event.code.includes('unauthorizedApplexUser') ) {
              cidErrorCode = 'InvalidTenantId';
            }
            dispatch(doCidSessionFailure(cidErrorCode, { errorCode: cidErrorCode, }, workflow, cidMobileServiceInfo.optionalTransactionId) );
          }
          else {
            console.log('Error when establishing applex connection. Redirect to error page.');
            dispatch(doSessionFailure(event.code) );
            if (isHelpdeskVerificationWorkflow) {
              dispatch(push('/helpdesk-verification-finish') );
            }
            else {
              dispatch(push('/autherror') );
            }
          }
          // falls through
        default:
            console.log('Connection event: '); // eslint-disable-line
            console.log(event); // eslint-disable-line
        }
      }
    };

    const oneSignAuthEventHandler = {
      onImprivataIdPushCompleted: function (result) {
        console.log('SessionVersion: ' + sessionVersion + ' expected: ' + expectedSessionVersion);
        if (expectedSessionVersion !== sessionVersion) {
          console.log('Old session version - not switching to manual'); // eslint-disable-line
          return;
        }
        console.log('onImprivataPushCompleted event'); // eslint-disable-line
        const authState = StartPushCompletedState.getValue(result.authenticationState);
        this.processAuthState(authState);
      },

      onIdvCompleted: function (result) {
        console.log('SessionVersion: ' + sessionVersion + ' expected: ' + expectedSessionVersion);
        if (expectedSessionVersion !== sessionVersion) {
          console.log('Old session version - not switching to manual'); // eslint-disable-line
          return;
        }
        console.log('onIdvCompleted event'); // eslint-disable-line
        const authState = StartIdvCompletedState.getValue(result.authenticationState);
        this.processAuthState(authState);
      },

      processAuthState: function (authState) {
        const { ignoreTimeout, isDeviceManagementWorkflow, } = getState().login;
        dispatch(startLoading() );
        dispatch(setServerState(authState) );
        if (authState.errorCode) {
          if (authState.errorCode === GetMsgInvalidCredentials.asString
            || authState.errorCode === GetMsgInternalError.asString) {
            dispatch(doLogout() );
            dispatch(doImprivataIdPushFailure(authState.errorCode) );
          }
          else if (!ignoreTimeout) {
            dispatch(decideNextStep(authState, session && !isDeviceManagementWorkflow) );
          }
        }
        else {
          dispatch(decideNextStep(authState, session && !isDeviceManagementWorkflow) );
        }
        if (ignoreTimeout) {
          dispatch(finishLoading() );
        }
      },
    };
    // This helper wraps the handler above so it can be accessed in scala, along with the protocol it represents
    const authProtocolAndHandler = CloudAuthApplexClient.ProtocolInterplexOnesignAuthV15OneSignAuthProtocolEventHandler.getProtocolAndHandler(oneSignAuthEventHandler);
    let networkString = `aws:${cloudauthEnvironment}`;
    if (cloudauthEnvironment.startsWith('docker:') ) {
      networkString = cloudauthEnvironment;
    }

    const client = new CloudAuthApplexClient.CloudAuthClient(
      accountToken,
      navigator.userAgent,
      new CloudAuthApplexClient.ClientConfig(
        new CloudAuthApplexClient.LoggingConfig('info', false),
        new CloudAuthApplexClient.NetworkingConfig(networkString)
      ),
      onConnectionListener,
      [authProtocolAndHandler,]
    );
    console.log('Setting sessionVersion to ' + sessionVersion + ' from ' + expectedSessionVersion); // eslint-disable-line

    // tracking CAUI version
    console.log('CAUI version ' + packageInfo.version); // eslint-disable-line

    expectedSessionVersion = sessionVersion;
    return client.startNewSession()
      .then(serverSession => {
        dispatch(doSetSession(serverSession) );
        dispatch(startNewSessionHandler(decodedToken, username, workflow, cidMobileServiceInfo) );
        // Wait for the stack to clear, then try delete any expired tokens - it's not an essential operation
        queueTokenDeletion();
        queueStateTokenDeletion();
      })
      .catch(ex => {
        // need to check if we got data for cidMobileServiceInfo
        // because we do not have sessionService data at this point
        if (cidMobileServiceInfo.optionalAppName !== undefined && cidMobileServiceInfo.optionalPartnerName !== undefined) {
          let cidErrorCode = 'InternalError';
          // if we get one of these error codes, we are assuming it is InvalidTenantId
          // according to my sources, the only case where ConfirmID Mobile could get these error is if Tenant ID is invalid.
          // -MDH
          if (Object.values(ex).includes('unauthorizedError') || Object.values(ex).includes('invalidToken') ) {
            cidErrorCode = 'InvalidTenantId';
          }
          dispatch(doCidSessionFailure(cidErrorCode, { errorCode: cidErrorCode, }, workflow, cidMobileServiceInfo.optionalTransactionId) );
        }
        else {
          dispatch(doSessionFailure(ex) );
          if (isHelpdeskVerificationWorkflow) {
            dispatch(push('/helpdesk-verification-finish') );
          }
          else {
            dispatch(push('/autherror') );
          }
        }
        console.log(ex); // eslint-disable-line

      });
  };
}

function startNewSessionHandler(decodedToken, username, workflow, cidMobileServiceInfo) {
  return function (dispatch, getState) {
    const StartInterplexId = StartSessionProtocols.StartInterplexId;
    const {
      oneSignTenantId, oneSignUserId, sessionService, resumeAuthFlowTokenUrl,
      forceMfa = false, session, errorCode, firstAttempt, initialUsername, initialPassword,
      stateToken, isHelpdeskVerificationWorkflow,
    } = getState().login;
    if (session && sessionService) {
      if (session.code) {
        dispatch(doSessionFailure(session.code) );
        if (isHelpdeskVerificationWorkflow) {
          dispatch(push('/helpdesk-verification-finish') );
        }
        else {
          dispatch(push('/autherror') );
        }
      }
      else {
        let newAuthClient = session.getProxiedInterplexClient(StartInterplexId);
        if (oneSignTenantId && oneSignUserId) {
          newAuthClient = getAuthClientWithAddress(newAuthClient, oneSignTenantId, oneSignUserId);
        }
        let authAppId = null;
        let authInstanceId = null;
        let cloudAuthTenantId = null;
        // Parkland cusom entry
        let useCustomUX = false;
        if (decodedToken && decodedToken.contextData) {
          cloudAuthTenantId = decodedToken.tenantId;
          authAppId = decodedToken.contextData.authAppId;
          authInstanceId = decodedToken.contextData.authInstanceId;
          // Parkland cusom entry
          useCustomUX = decodedToken.useCustomUX;
        }

        let workflowValue = (sessionService === sba || sessionService === piw) ? decodedToken.workflow : workflow;
        dispatch(setOptionalWorkflowNameAndRequestContext(
          workflowValue || null,
          decodedToken.requestContext || null
        ) );
        // Parkland custom argument
        dispatch(doSessionStart(session, expectedSessionVersion + 1, newAuthClient, authAppId, authInstanceId, cloudAuthTenantId, useCustomUX, initialModality(sessionService) ) );
        dispatch(doStartSessionEstablishing(false) );
        if (sessionService === sba) {
          if (stateToken) {
            dispatch(push('/resume-auth-session') );
          }
          else if (resumeAuthFlowTokenUrl && !forceMfa) {
            dispatch(push('/resume-auth-flow') );
          }
          else if (skipUsernameScreen(username, workflowValue, decodedToken.requestContext, errorCode, firstAttempt) ) {
            setTimeout( () => dispatch(doStartAuthSessionOnly(username) ), 100);
          }
          else {
            dispatch(push('/username') );
          }
        }
        else if (sessionService === cidMobile) {
          setTimeout( () => dispatch(doStartAuthCidSession(username, workflowValue, cidMobileServiceInfo) ), 100);
        }
        else if (sessionService === cidra) {
          if (sessionService === cidra && initialUsername?.trim().length > 0 && initialPassword?.trim().length > 0) {
            dispatch(setCidraAdvancedUi() );
          }
          dispatch(push('/username') );
        }
        else if (sessionService === piw) {
          if (stateToken) {
            dispatch(startLoading() );
            dispatch(push('/resume-auth-session') );
          }
          else {
            dispatch(push('/username') );
          }
        }
        else {
          dispatch(push('/username-password') );
        }
      }
    }
  };
}

function setCidraAdvancedUi() {
  return {
    type: types.SET_CIDRA_ADVANCED_UI,
    cidraAdvancedUi: true,
  };
}

function skipUsernameScreen(username, workflow, requestContext, errorCode, firstAttempt) {
  return username && errorCode === null && firstAttempt
    && (workflow === EPCS_WORKFLOW || isEntraIdEamIntegration(workflow, requestContext) );
}

function isEntraIdEamIntegration(workflow, requestContext) {
  return workflow === SECONDFACTORONLY_WORKFLOW && requestContext
    && (requestContext.ACR_VALUE === POSSESSION_OR_INHERENCE_ACR_VALUE
      || requestContext.ACR_VALUE === KNOWLEDGE_OR_POSSESSION_ACR_VALUE
      || requestContext.ACR_VALUE === KNOWLEDGE_OR_INHERENCE_ACR_VALUE
      || requestContext.ACR_VALUE === KNOWLEDGE_OR_POSSESSION_OR_INHERENCE_ACR_VALUE
      || requestContext.ACR_VALUE === KNOWLEDGE_ACR_VALUE
      || requestContext.ACR_VALUE === POSSESSION_ACR_VALUE
      || requestContext.ACR_VALUE === INHERENCE_ACR_VALUE);
}

export function doLoginStart() {
  return { type: types.LOGIN_STARTED, };
}

/**
 * Client side timeout handling while we have no way for the server to tell the UI
 * that a session timed out.
 *
 * After SERVER_TIMEOUT ms from startAuth, the session will time out.
 * The timeout does not get refreshed after each server round trip at present.
 *
 * isCidSession: can't be guaranteed set from response. needs to be set from input params (not login state)
 */
export function startTimeoutTimer(currentSessionVersion, isCidSession) {
  return function (dispatch, getState) {
    setTimeout( () => {
      const {
        authenticationToken, sessionVersion, workflow, cidMobileServiceInfo,
      } = getState().login;
      // Timeout if and only if the session version hasn't changed
      // (user hasn't logged out or failed auth)
      // AND user doesn't have an authentication token already
      // (and the UI therefore doesn't have to talk to the server anymore -
      // it just needs to submit the credentials to the VPN)
      console.log('sessionVersion: ' + sessionVersion + ' currentSessionVersion: ' + currentSessionVersion); // eslint-disable-line
      if (sessionVersion === currentSessionVersion && !authenticationToken) {
        if (isCidSession) {
          // needs to use the specific timeout string here
          const errorCodeString = 'AuthTimeOut';
          dispatch(doCidSessionFailure(errorCodeString, { errorCode: errorCodeString, }, workflow, cidMobileServiceInfo.optionalTransactionId) );
        }
        else {
          dispatch(doLogout() );
          dispatch(doTimeoutFailure() );
        }
      }
    }, SERVER_TIMEOUT);
  };
}

export function doTimeoutFailure() {
  const errorMessage = getErrorMessage('timeout');
  return {
    errorCode: 'AuthTimeOut',
    errorMessage,
    type: types.LOGIN_FAIL,
    fromUsernameScreen: false,
  };
}

export function doStartAuthSession(username, credential) {
  return function (dispatch, getState) {
    const { StartAuthSession, StartResponseStartAuthenticationSessionSucceeded, } = StartSessionProtocols;
    const {
      sessionVersion, authClient, sessionService, authAppId, authInstanceId = null, forceMfa = false, initialUsername,
      optionalWorkflowName = null, requestContext = null,
    } = getState().login;
    dispatch(startLoading() );
    dispatch(startTimeoutTimer(sessionVersion, false) );
    if (initialUsername) {
      dispatch(setInitialUsername(username) );
    }
    const authSessionParams =
      new StartAuthSession(sessionService, authAppId, username,
        authInstanceId, forceMfa, null, optionalWorkflowName, null, requestContext, null);
    return authClient.sendRequest(authSessionParams).then(responseWithMetadata => {
      const result = responseWithMetadata.message;
      const sourceAddress = responseWithMetadata.metadata.sourceInfo;
      if (result.responseType === StartResponseStartAuthenticationSessionSucceeded) {

        const enterpriseId = result.enterpriseId ? (typeof result.enterpriseId === 'string' ? result.enterpriseId : result.enterpriseId.value) : null;
        const subscriberId = result.userId ? (typeof result.userId === 'string' ? result.userId : result.userId.value) : null;

        if (enterpriseId !== null && subscriberId !== null) {
          dispatch(setEnterpriseId(enterpriseId) );
          dispatch(setSubscriberId(subscriberId) );

          const currentSSFToken = doGetSkipSecondFactorToken(enterpriseId, subscriberId);
          if (currentSSFToken != null) {
            logSsf('SSF token not null for this enterprise/user combination, so setting it in the state: ' + currentSSFToken);
            dispatch(setSkipSecondFactorToken(currentSSFToken) );
          }
        }
        else {
          logSsf('Enterprise and/or user id null - skipping looking for SSF token');
        }
        dispatch(setServerState(result.authenticationState) );
        const capabilitiesList = result.capabilities;
        const capabilities = capabilitiesListToObject(capabilitiesList);
        const oneSignSessionId = result.sessionId;
        const newAuthClient = authClient.withNewTargetAddress(sourceAddress);
        if (capabilities.DeviceManagement !== undefined) {
          dispatch(doSetSelfServiceVersion('1') );
        }
        dispatch(doStartAuthSessionSuccess(username, capabilities, oneSignSessionId, newAuthClient) );
        dispatch(doVerifyPassword(credential) );

      }
      else {
        // got StartAuthenticationSessionFailed
        dispatch(setServerState(undefined) );
        dispatch(doLogout() );
        dispatch(doLoginFailure(result.errorCode) );
      }
    })
      .catch(ex => {
        dispatch(doLogout() );
        dispatch(doLoginFailure(ex) );
      });
  };
}

export function doStartAuthSessionOnly(username) {
  return function(dispatch, getState) {
    const { StartAuthSession, StartResponseStartAuthenticationSessionSucceeded, } = StartSessionProtocols;
    const {
      sessionVersion, authClient, sessionService, authAppId, authInstanceId = null, forceMfa = false, initialUsername,
      optionalWorkflowName = null, requestContext = null, isEpcsWorkflow = false, cidraAdvancedUi,
    } = getState().login;
    dispatch(startLoading() );
    if (initialUsername) {
      dispatch(setInitialUsername(username) );
    }
    dispatch(startTimeoutTimer(sessionVersion, false) );
    const authSessionParams =
      new StartAuthSession(sessionService, authAppId, username,
        authInstanceId, forceMfa, null, optionalWorkflowName, null, requestContext, null);
    return authClient.sendRequest(authSessionParams).then(responseWithMetadata => {
      const result = responseWithMetadata.message;
      const sourceAddress = responseWithMetadata.metadata.sourceInfo;
      if (result.responseType === StartResponseStartAuthenticationSessionSucceeded) {

        const enterpriseId = result.enterpriseId ? (typeof result.enterpriseId === 'string'? result.enterpriseId : result.enterpriseId.value) : null;
        const subscriberId = result.userId ? (typeof result.userId === 'string'? result.userId : result.userId.value) : null;

        if (enterpriseId !== null && subscriberId !== null) {
          dispatch(setEnterpriseId(enterpriseId) );
          dispatch(setSubscriberId(subscriberId) );

          const currentSSFToken = doGetSkipSecondFactorToken(enterpriseId, subscriberId);
          if (currentSSFToken != null) {
            logSsf('SSF token not null for this enterprise/user combination, so setting it in the state: ' + currentSSFToken);
            dispatch(setSkipSecondFactorToken(currentSSFToken) );
          }
        }
        else {
          logSsf('Enterprise and/or user id null - skipping looking for SSF token');
        }
        dispatch(setServerState(result.authenticationState) );
        const capabilitiesList = result.capabilities;
        const capabilities = capabilitiesListToObject(capabilitiesList);
        const oneSignSessionId = result.sessionId;
        const newAuthClient = authClient.withNewTargetAddress(sourceAddress);
        if(capabilities.DeviceManagement !== undefined) {
          dispatch(doSetSelfServiceVersion('1') );
        }
        dispatch(doStartAuthSessionSuccess(username, capabilities, oneSignSessionId, newAuthClient) );
        dispatch(startLoading() );
        const {
          ModalImprivataId, ModalIdOutOfBand, ModalEmailOtp, ModalIdToken, ModalIdIdv,
        } = ChooseModalityProtocols;
        if (sessionService === cidra && cidraAdvancedUi) {
          dispatch(processCidraAdvancedUiFlow(result.authenticationState) );
          return;
        }
        if (result.authenticationState.temporaryCodeAllowed
          && result.authenticationState.temporaryCodeAvailable
          && (
            result.authenticationState.currentModality === ModalImprivataId.asString
            || result.authenticationState.currentModality === ModalIdOutOfBand.asString
            || result.authenticationState.currentModality === ModalEmailOtp.asString
            || result.authenticationState.currentModality === ModalIdToken.asString
          )
        ) {
          dispatch(doSetInitialTempCode(true) );
          dispatch(decideNextStep(result.authenticationState, false) );
          return;
        }
        if(capabilities.CommonAuthenticationUI !== undefined
          && capabilities.CommonAuthenticationUI.some(capabilitiy => capabilitiy.capabilityName === 'faceAsFirstFactor')
          && capabilities.CommonAuthenticationUI.some(capabilitiy => capabilitiy.capabilityName === 'iidAsFirstFactor')
          && capabilities.CommonAuthenticationUI.some(capabilitiy => capabilitiy.capabilityName === 'smsAsFirstFactor')
          && capabilities.CommonAuthenticationUI.some(capabilitiy => capabilitiy.capabilityName === 'emailAsFirstFactor')
        ) {
          switch (result.authenticationState.currentModality) {
          case ModalImprivataId.asString:
            dispatch(initiateImprivataIdPush() );
            break;
          case ModalIdOutOfBand.asString:
            dispatch(resendSms() );
            break;
          case ModalEmailOtp.asString:
            dispatch(resendEmail() );
            break;
          case ModalIdIdv.asString:
            dispatch(initiateIdv() );
            break;
          default:
            dispatch(decideNextStep(result.authenticationState, false) );
          }
        }
        else {
          switch (result.authenticationState.currentModality) {
          case ModalImprivataId.asString:
            dispatch(verifyPassword('push') );
            break;
          case ModalIdOutOfBand.asString:
            dispatch(verifyPassword('sms') );
            break;
          case ModalEmailOtp.asString:
            dispatch(verifyPassword('email') );
            break;
          default:
            dispatch(decideNextStep(result.authenticationState, false) );
          }
        }
      }
      else {
        // got StartAuthenticationSessionFailed
        const { GetMsgWorkflowNotEnabled, GetMsgWorkflowNotAllowed, } = GetMessageProtocols;
        const isEpcsError =
          result.errorCode === GetMsgWorkflowNotEnabled.asString || result.errorCode === GetMsgWorkflowNotAllowed.asString;
        if (isEpcsError && isEpcsWorkflow) {
          dispatch(setInitialUsername(username) );
        }
        dispatch(setServerState(undefined) );
        dispatch(doLogout() );
        dispatch(doLoginFailure(result.errorCode) );
      }
    })
      .catch(ex => {
        dispatch(doLogout() );
        dispatch(doLoginFailure(ex) );
      });
  };
}

function processCidraAdvancedUiFlow(authenticationState) {
  return function (dispatch) {
    const { ModalIdPassword, } = ChooseModalityProtocols;
    const { currentModality, } = authenticationState;
    if (currentModality !== ModalIdPassword.asString) {
      dispatch(doChangeModality(currentModality.toLowerCase(), ModalIdPassword.asString.toLowerCase() ) );
    }
    else {
      dispatch(decideNextStep(authenticationState, false) );
    }
  };
}

// different start auth for Confirm Id
export function doStartAuthCidSession(username, workflow, cidMobileServiceInfo) {
  return function (dispatch, getState) {
    const {
      StartAuthSession,
      StartResponseStartAuthenticationSessionSucceeded,
      StartTokenAuthState,
      StartTokenAuthStateTypeIdTokenChallenge,
    } = StartSessionProtocols;
    const {
      sessionVersion, authClient, sessionService, authAppId, authInstanceId = null,
    } = getState().login;
    dispatch(startLoading() );
    dispatch(startTimeoutTimer(sessionVersion, true) );

    // //test info
    // const cidMobileServiceInfo = {
    //   optionalPartnerName: "Epic",
    //   optionalAppName: "Haiku",
    //   optionalTransactionId: null
    // };
    dispatch(setCidMobileServiceInfo(cidMobileServiceInfo) );

    const authSessionParams = new StartAuthSession(sessionService, authAppId, username, authInstanceId, false, null, workflow, cidMobileServiceInfo, null, null);
    console.log('Starting Cid Auth session:');
    console.log(authSessionParams);
    return authClient.sendRequest(authSessionParams).then(responseWithMetadata => {
      // tests ! result is always undefined
      const result = responseWithMetadata.message;
      const sourceAddress = responseWithMetadata.metadata.sourceInfo;

      if (result.responseType === StartResponseStartAuthenticationSessionSucceeded) {

        const enterpriseId = result.enterpriseId ? (typeof result.enterpriseId === 'string' ? result.enterpriseId : result.enterpriseId.value) : null;
        const subscriberId = result.userId ? (typeof result.userId === 'string' ? result.userId : result.userId.value) : null;

        if (enterpriseId !== null && subscriberId !== null) {
          dispatch(setEnterpriseId(enterpriseId) );
          dispatch(setSubscriberId(subscriberId) );

          const currentSSFToken = doGetSkipSecondFactorToken(enterpriseId, subscriberId);
          if (currentSSFToken != null) {
            logSsf('SSF token not null for this enterprise/user combination, so setting it in the state: ' + currentSSFToken);
            dispatch(setSkipSecondFactorToken(currentSSFToken) );
          }
        }
        else {
          logSsf('Enterprise and/or user id null - skipping looking for SSF token');
        }
        dispatch(setServerState(result.authenticationState) );
        const capabilitiesList = result.capabilities;
        const capabilities = capabilitiesListToObject(capabilitiesList);
        const oneSignSessionId = result.sessionId;
        const newAuthClient = authClient.withNewTargetAddress(sourceAddress);
        if (capabilities.DeviceManagement !== undefined) {
          dispatch(doSetSelfServiceVersion('1') );
        }
        dispatch(doStartAuthSessionSuccess(username, capabilities, oneSignSessionId, newAuthClient) );
        // dispatch(doVerifyPassword(credential));
        console.log('start cidmobile auth:');

        switch (result.authenticationState.currentModality) {
        case 'IdToken':
          let challengeText = null;
          if (result.authenticationState.currentModality.optionalIdTokenAuthenticationState !== undefined) {
            const idTokenData = StartTokenAuthState.getValue(result.modalities.optionalIdTokenAuthenticationState);
            if (idTokenData.idTokenAuthenticationStateType === StartTokenAuthStateTypeIdTokenChallenge) {
              challengeText = idTokenData.idTokenChallenge;
            }
          }
          dispatch(doInputOtp(challengeText) );
          dispatch(push('/otp') );
          break;
        case 'Password':
          dispatch(doInputPassword() );
          dispatch(push('/password') );
          break;
        default:
          console.log('Modality failed: ' + result.errorCode);
          dispatch(doCidSessionFailure(result.errorCode, result, workflow, cidMobileServiceInfo.optionalTransactionId) );
          break;
        }

      }
      else {
        console.log('StartAuthCidSession failed: ' + result.errorCode);
        dispatch(setServerState(undefined) );
        dispatch(doCidSessionFailure(result.errorCode, result, workflow, cidMobileServiceInfo.optionalTransactionId) );
      }
    })
      .catch( () => {
        console.log('Session Error ');
        dispatch(doCidSessionFailure('InternalError', { errorCode: 'InternalError', }, workflow, cidMobileServiceInfo.optionalTransactionId) );
      });
  };
}

// confirmID session failures need to set CID error info
export function doCidSessionFailure(errorCode, errorCodeObj, workflow, transactionId) {
  return function (dispatch) {

    dispatch(setCidErrorInfo(errorCode, getCidMobileErrorString(errorCode, workflow, transactionId) ) );
    dispatch(doSessionFailure(errorCodeObj) );
    dispatch(push('/waitingforemr') );
  };
}

export function doSessionFailure(sessionException) {
  return {
    sessionException,
    type: types.SESSION_FAILED,
  };
}

// Also adding Parkland argument here
export function doSessionStart(session, sessionVersion, authClient, authAppId, authInstanceId, cloudAuthTenantId, useCustomUX, modality) {
  return {
    type: types.SESSION_STARTED,
    sessionVersion,
    session,
    authClient,
    authAppId,
    authInstanceId,
    cloudAuthTenantId,
    useCustomUX,
    modality,
  };
}

export function doSetSession(session) {
  return {
    type: types.SESSION_SET,
    session,
  };
}

export function doStartSessionEstablishing(startSessionEstablishing) {
  return {
    type: types.START_SESSION_ESTABLISHING,
    startSessionEstablishing,
  };
}

export function doSessionConnected(oneSignTenantId, oneSignUserId, sessionService, authClient) {
  return {
    type: types.SESSION_CONNECTED,
    oneSignTenantId,
    oneSignUserId,
    sessionService,
    authClient,
  };
}

export function doImprivataIdPushFailure(errorCode) {
  const errorMessage = getErrorMessage(errorCode, IID_PUSH);
  return {
    errorCode,
    errorMessage,
    type: types.LOGIN_FAIL,
  };
}

export function doStartAuthSessionSuccess(username, capabilities, oneSignSessionId, authClient) {
  return {
    type: types.SESSION_AUTH_STARTED,
    username,
    capabilities,
    oneSignSessionId,
    authClient,
  };
}

export function setEnterpriseId(enterpriseId) {
  return {
    type: types.SET_ENTERPRISE_ID,
    enterpriseId,
  };
}

export function setSubscriberId(subscriberId) {
  return {
    type: types.SET_SUBSCRIBER_ID,
    subscriberId,
  };
}

export function setCidMobileServiceInfo(cidMobileServiceInfo) {
  return {
    type: types.SET_CID_MOBILE_SERVICE_INFO,
    cidMobileServiceInfo,
  };
}

export function setCidErrorInfo(cidErrorCode, cidErrorMessage) {
  return {
    type: types.SET_CID_ERROR_INFO,
    cidErrorCode,
    cidErrorMessage,
  };
}

/**
 * Converts the list of capabilities provided by the appliance into a javascript object.
 * It is expected that the format of the json string in the list will be the following:
 * { "name": "CapabilityName", "data": "CapabilityData" }
 *
 * @param {string[]} list The array of JSON strings that represent each capability
 * and any data needed by the UI to display correctly
 */
export function capabilitiesListToObject(list) {
  var capabilities = {};
  /** Try doing it the new way (Fortuna+) */
  for (var i = 0; i < list.length; i++) {
    var json = JSON.parse(list[i]);
    capabilities[json.name] = json.capabilities;
    /** If that fails, try doing it the old way (Europa-) */
    if (json.capabilities == null) {
      for (var k = 0; k < list.length; k++) {
        try {
          json = JSON.parse(list[i]);
          capabilities[json.name] = json.data;
        }
        catch (error) {
          capabilities[list[i]] = JSON.parse('version: "1"');
          break;
        }
      }
    }
  }
  if (capabilities.DeviceManagement !== undefined) {
    doSetSelfServiceVersion('1');
  }
  return capabilities;
}

export function doSetSelfServiceVersion(selfServiceVersion) {
  return {
    type: types.SET_SELF_SERVICE_VERSION,
    selfServiceVersion: selfServiceVersion,
  };
}

// setting to change initial modality for cidmobile only
// input: sessionService name
export function initialModality(sessionService) {
  if (sessionService === cidMobile) {
    return 'password';
  }
  else {
    return 'username-password';
  }
}

export function doSetSessionTraceId(sessionTraceId) {
  return {
    type: types.SET_SESSION_TRACE_ID,
    sessionTraceId,
  };
}
